import React from 'react';
import formStyle from 'acscp/components/flightRequest/form/flightRequestForm.module.scss';
import style from 'acscp/components/flightRequest/form/flightRequestLegForm.module.scss';
import {
    datePickerFormat,
    FlightRequestFormLeg,
    timePickerFormat
} from 'acscp/components/flightRequest/form/helpers/flightRequestFormMapper';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import { Airport } from 'acscp/store/airportsState';
import { AirportTypeahead } from './airportTypeahead';
import classNames from 'classnames';
import { FormValidator, ValidationRule } from 'acscp/components/common/helpers/formValidator';
import {
    DateValidationChecks,
    NumberValidationChecks,
    StringValidationChecks
} from 'acscp/components/common/helpers/validationChecks';
import { InputWithValidation } from 'acscp/components/common/validation/inputWithValidation';
import { getDisplayTextForAirport } from './helpers/airportHelper';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { LocaleCode } from 'acscp/translations';
import { connect } from 'react-redux';
import { AppState, DateTime } from 'acscp/store';
import { CrossIcon } from 'acscp/components/common/icons/crossIcon';
import { PlusIcon } from 'acscp/components/common/icons/plusIcon';
import { MinusIcon } from 'acscp/components/common/icons/minusIcon';
import { ButtonLaunchingPopover } from 'acscp/components/common/buttonLaunchingPopover';
import { NotesIcon, NotesIconVariation } from 'acscp/components/common/icons/notesIcon';
import { DateStyle, FormattedDate } from 'acscp/components/common/formattedDate';
import { ArrowIcon } from 'acscp/components/common/icons/arrowIcon';
import { getDefaultTimeFormatForLocaleOrFallback } from 'acscp/store/helpers/dateTimeHelper';

const MAX_LEG_NOTES_CHARACTERS = 500;

const MAX_VALID_DEPARTURE_DATE = moment().add({year: 10}).endOf('year');

const translations = defineMessages({
    departureAirportRequired: {
        id: 'components.flightRequest.form.flightRequestLegForm.departureAirportRequired',
        defaultMessage: 'Departure airport is required'
    },
    arrivalAirportRequired: {
        id: 'components.flightRequest.form.flightRequestLegForm.arrivalAirportRequired',
        defaultMessage: 'Arrival airport is required'
    },
    dateTypeDepartOn: {
        id: 'components.flightRequest.form.flightRequestLegForm.departOn',
        defaultMessage: 'Depart On'
    },
    dateTypeArriveOn: {
        id: 'components.flightRequest.form.flightRequestLegForm.arriveOn',
        defaultMessage: 'Arrive On'
    },
    datePlaceholder: {
        id: 'components.flightRequest.form.flightRequestLegForm.datePlaceholder',
        defaultMessage: 'Date'
    },
    dateRequired: {
        id: 'components.flightRequest.form.flightRequestLegForm.validDateRequired',
        defaultMessage: 'Valid date is required'
    },
    timePlaceholder: {
        id: 'components.flightRequest.form.flightRequestLegForm.timePlaceholder',
        defaultMessage: 'Time'
    },
    numberOfPassengersRequired: {
        id: 'components.flightRequest.form.flightRequestLegForm.numberOfPassengersRequired',
        defaultMessage: 'Number of passengers is required'
    },
    cannotBeBeforePreviousLeg: {
        id: 'components.flightRequest.form.flightRequestLegForm.cannotTravelBeforePreviousLeg',
        defaultMessage: 'Flight cannot be before previous leg'
    },
    dateCannotBeInPast: {
        id: 'components.flightRequest.form.flightRequestLegForm.dateNotInPast',
        defaultMessage: 'Flight cannot be in the past'
    },
    dateCannotBe10YearsInFuture: {
        id: 'components.flightRequest.form.flightRequestLegForm.dateNot10YearsInFuture',
        defaultMessage: 'Flight must be within the next 10 years'
    },
    notesPlaceholder: {
        id: 'components.flightRequest.form.flightRequestLegForm.notesPlaceholder',
        defaultMessage: 'Please add any additional requests, for example catering requirements, time slots and date or airport flexibility.'
    }
});

interface FlightRequestLegFormProps {
    leg: FlightRequestFormLeg;
    previousLegDate: string;
    previousLegTime: string;
    registerValidationCallback: (validateLeg: () => boolean) => void;
    onChange: (leg: FlightRequestFormLeg) => void;
    showRemoveButton: boolean;
    canRemove: boolean;
    onRemove: () => void;
    showDoneButton: boolean;
    onDone: () => void;
}

interface InjectedStateProps {
    currentLocale: LocaleCode;
}

class FlightRequestLegFormWithIntl
    extends React.Component<FlightRequestLegFormProps & InjectedStateProps & InjectedIntlProps> {
    private validator: FormValidator;

    constructor(props: FlightRequestLegFormProps & InjectedStateProps & InjectedIntlProps) {
        super(props);
        this.validator = new FormValidator(this.getValidationRules());
    }

    componentDidMount(): void {
        this.props.registerValidationCallback(() => this.validator.isStateValid());
    }

    private getValidationRules(): ValidationRule[] {
        const dateCannotBeInPastMessage = this.props.intl.formatMessage(translations.dateCannotBeInPast);
        const dateCannotBe10YearsInFutureMessage = this.props.intl.formatMessage(translations.dateCannotBe10YearsInFuture);
        const legCannotBeBeforePreviousLegMessage = this.props.intl.formatMessage(translations.cannotBeBeforePreviousLeg);

        return [
            {
                validatedPropertyName: 'date',
                validationCheck: DateValidationChecks.isRequired(() => this.props.leg.date),
                errorMessage: this.props.intl.formatMessage(translations.dateRequired)
            },
            {
                validatedPropertyName: 'date',
                validationCheck: DateValidationChecks.dateIsNotBeforeToday(() => this.props.leg.date),
                errorMessage: dateCannotBeInPastMessage
            },
            {
                validatedPropertyName: 'date',
                validationCheck: DateValidationChecks.dateIsNotAfter(
                    () => MAX_VALID_DEPARTURE_DATE.format('YYYY-MM-DD'),
                    () => this.props.leg.date
                ),
                errorMessage: dateCannotBe10YearsInFutureMessage
            },
            {
                validatedPropertyName: 'time',
                validationCheck: !DateValidationChecks.dateIsNotBeforeToday(() => this.props.leg.date) ||
                    DateValidationChecks.timeIsNotInPast(() => this.props.leg.date, () => this.props.leg.time),
                errorMessage: dateCannotBeInPastMessage
            },
            {
                validatedPropertyName: 'date',
                validationCheck: DateValidationChecks.dateIsNotBefore(() => this.props.previousLegDate, () => this.props.leg.date),
                errorMessage: legCannotBeBeforePreviousLegMessage
            },
            {
                validatedPropertyName: 'time',
                validationCheck: DateValidationChecks.timeIsNotBefore(
                    () => this.props.previousLegDate, () => this.props.previousLegTime, () => this.props.leg.date, () => this.props.leg.time
                ),
                errorMessage: legCannotBeBeforePreviousLegMessage
            },
            {
                validatedPropertyName: 'departureAirport',
                validationCheck:
                    StringValidationChecks.isRequired(
                        () => this.props.leg.departureAirport ?
                            getDisplayTextForAirport(this.props.leg.departureAirport) :
                            this.props.leg.departureLocation
                    ),
                errorMessage: this.props.intl.formatMessage(translations.departureAirportRequired)
            },
            {
                validatedPropertyName: 'arrivalAirport',
                validationCheck:
                    StringValidationChecks.isRequired(
                        () => this.props.leg.arrivalAirport ?
                            getDisplayTextForAirport(this.props.leg.arrivalAirport) :
                            this.props.leg.arrivalLocation
                    ),
                errorMessage: this.props.intl.formatMessage(translations.arrivalAirportRequired)
            },
            {
                validatedPropertyName: 'passengers',
                validationCheck: () => NumberValidationChecks.isValidNumber(() => this.props.leg.passengers)
                        && this.props.leg.passengers >= 1,
                errorMessage: this.props.intl.formatMessage(translations.numberOfPassengersRequired)
            }
        ];
    }

    getDefaultChangeHandler = (modifier?: (value: string) => any) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            const value = modifier ? modifier(event.target.value) : event.target.value;

            const leg = {
                ...this.props.leg,
                [event.target.name]: value
            };
            this.props.onChange(leg);
    }

    airportSelectedHandler = (selectedAirport: Airport, elementName: string) => {
        const leg = {
            ...this.props.leg,
            [`${elementName}Airport`]: selectedAirport,
            [`${elementName}Location`]: null
        };
        this.props.onChange(leg);
    }

    airportFreetextInputChangedHandler = (inputValue: string, elementName: string) => {
        const leg = {
            ...this.props.leg,
            [`${elementName}Airport`]: null,
            [`${elementName}Location`]: inputValue
        };
        this.props.onChange(leg);
    }

    useDepartureDateChangeHandler = (newUseDepartureDate: boolean) => {
        const updatedLeg = {
            ...this.props.leg,
            useDepartureDate: newUseDepartureDate
        };
        this.props.onChange(updatedLeg);
    }

    datePickerChangeHandler = (input: Date | null) => {
        const dateStringToStore = !input ? '' : moment(input).format(datePickerFormat);
        const updatedLeg = {
            ...this.props.leg,
            date: dateStringToStore
        };
        this.props.onChange(updatedLeg);
    }

    timePickerChangeHandler = (input: Date | null) => {
        const timeStringToStore = !input ? '' : moment(input).format(timePickerFormat);
        const updatedLeg = {
            ...this.props.leg,
            time: timeStringToStore
        };
        this.props.onChange(updatedLeg);
    }

    passengerChangeButtonClicked = (newPassengers: number) => {
        const newValue = newPassengers < 1 ? 1 : newPassengers;
        const updatedLeg = {
            ...this.props.leg,
            passengers: newValue
        };
        this.props.onChange(updatedLeg);
    }

    renderDateTypeOptions = (useDepartureDate: boolean, onChange: (newUseDepartureDate: boolean) => void) => {
        const {intl} = this.props;

        return (
            <div className={style.DateTypeOptions}>
                <label className={classNames(style.Input, {[style.SelectedDateType]: useDepartureDate})} tabIndex={0}>
                    {this.props.intl.formatMessage(translations.dateTypeDepartOn)}
                    <input
                        type="radio"
                        name="useDepartureDate"
                        value="true"
                        checked={useDepartureDate}
                        onChange={() => onChange(true)}
                    />
                </label>
                <label className={classNames(style.Input, {[style.SelectedDateType]: !useDepartureDate})} tabIndex={0}>
                    {this.props.intl.formatMessage(translations.dateTypeArriveOn)}
                    <input
                        type="radio"
                        name="useDepartureDate"
                        value="false"
                        checked={!useDepartureDate}
                        onChange={() => onChange(false)}
                    />
                </label>
            </div>
        );
    }

    renderDateTypePopoverContent = (useDepartureDate: boolean, closePopover: () => void): React.ReactNode => {
        return this.renderDateTypeOptions(useDepartureDate, (newUseDepartureDate: boolean) => {
            this.useDepartureDateChangeHandler(newUseDepartureDate);
            closePopover();
        });
    }

    renderDateInputPopoverContent = (
        value: Date | null, minDate: Date | null, maxDate: Date | null, closePopover: () => void
    ): React.ReactNode => {
        return (
            <div>
                <DatePicker
                    calendarClassName={style.DateTimePickerCalendar}
                    onChange={date => { this.datePickerChangeHandler(date); closePopover(); }}
                    selected={value}
                    locale={this.props.currentLocale}
                    inline={true}
                    minDate={minDate ?? new Date()}
                    maxDate={maxDate ?? undefined}
                    showDisabledMonthNavigation={true}
                    allowSameDay={true}
                />
            </div>
        );
    }

    renderTimeInputPopoverContent = (
            value: Date | null, minDate: Date | null, closePopover: () => void, locale: LocaleCode
        ): React.ReactNode => {
        return (
            <div>
                <DatePicker
                    showTimeSelect={true}
                    showTimeSelectOnly={true}
                    timeIntervals={30}
                    timeFormat={getDefaultTimeFormatForLocaleOrFallback(locale)}
                    calendarClassName={classNames(style.DateTimePickerCalendar, style.TimeOnlyPickerCalendar)}
                    onChange={time => { this.timePickerChangeHandler(time); closePopover(); }}
                    selected={value}
                    locale={this.props.currentLocale}
                    inline={true}
                />
            </div>
        );
    }

    renderNotesInputPopoverContent = (value: string, closePopover: () => void): React.ReactNode => {
        return (
            <div>
                <div className={style.LegNotesInputCount}>
                    <FormattedMessage
                        id={'components.flightRequest.form.flightRequestLegForm.characterCounter'}
                        defaultMessage={'{currentCount} of {maxCount} characters'}
                        values={{
                            currentCount: value.length,
                            maxCount: MAX_LEG_NOTES_CHARACTERS
                        }}
                    />
                </div>
                <textarea
                    className={classNames(style.Input, style.LegNotesInput)}
                    name="specialRequests"
                    maxLength={MAX_LEG_NOTES_CHARACTERS}
                    value={value}
                    onChange={this.getDefaultChangeHandler()}
                    placeholder={this.props.intl.formatMessage(translations.notesPlaceholder)}
                />
                {this.renderPopoverDoneButton(closePopover)}
            </div>
        );
    }

    renderPopoverDoneButton = (closePopover: () => void): React.ReactNode => (
        <div className={style.DoneButtonContainer}>
            <button type="button" onClick={closePopover} className={classNames(style.PopoverDoneButton)}>
                <FormattedMessage
                    id={'components.flightRequest.form.flightRequestLegForm.doneButton'}
                    defaultMessage={'DONE'}
                />
            </button>
        </div>
    )

    render(): JSX.Element {
        const {leg} = this.props;
        const {passengers, specialRequests, useDepartureDate,
            departureAirport, departureLocation, arrivalAirport, arrivalLocation} = leg;

        const selectedDate = !!leg.date ? moment(leg.date, datePickerFormat).toDate() : null;
        const selectedTime = !!leg.time ? moment(leg.time, timePickerFormat).toDate() : null;
        const previousLegDate = !!this.props.previousLegDate ? moment(this.props.previousLegDate, datePickerFormat).toDate() : null;
        const maxDate = MAX_VALID_DEPARTURE_DATE.toDate();
        const {intl} = this.props;
        const locale = this.props.currentLocale;
        const validationErrors = this.validator.getDisplayedErrors(leg.showValidationErrors, undefined);
        return (
            <div className={classNames(formStyle.Row)}>
                <div className={classNames(formStyle.Cell, formStyle.DepartureCell)}>
                    <div className={formStyle.CellContent}>
                        <AirportTypeahead
                            name="departure"
                            selectedAirport={departureAirport}
                            alternativeLocation={departureLocation}
                            validationErrors={validationErrors}
                            airportSelectedHandler={this.airportSelectedHandler}
                            inputValueChangedHandler={this.airportFreetextInputChangedHandler}
                        />
                    </div>
                </div>
                <div className={classNames(formStyle.Cell, formStyle.ArrivalCell)}>
                    <div className={formStyle.CellContent}>
                        <AirportTypeahead
                            name="arrival"
                            selectedAirport={arrivalAirport}
                            alternativeLocation={arrivalLocation}
                            validationErrors={validationErrors}
                            airportSelectedHandler={this.airportSelectedHandler}
                            inputValueChangedHandler={this.airportFreetextInputChangedHandler}
                        />
                    </div>
                </div>
                <div className={classNames(formStyle.Cell, formStyle.DateTypeCell)}>
                    <div className={formStyle.CellContent}>
                        <ButtonLaunchingPopover
                            buttonClassName={classNames(style.Input, style.DateTypeButton)}
                            childComponentFactory={closePopover => this.renderDateTypePopoverContent(useDepartureDate, closePopover)}
                            popoverHorizontalAlignment="center"
                            popoverBodyClassName={classNames(style.Popover, style.DateTypePopover)}
                            closeOnClickOutside={true}
                            focusOnShow={true}
                        >
                            <div className={style.DateTypeButtonContent}>
                                {useDepartureDate
                                    ? this.props.intl.formatMessage(translations.dateTypeDepartOn)
                                    : this.props.intl.formatMessage(translations.dateTypeArriveOn)}
                                <ArrowIcon/>
                            </div>
                        </ButtonLaunchingPopover>
                        <div className={style.InlineDateTypeOptions}>
                            {this.renderDateTypeOptions(useDepartureDate, this.useDepartureDateChangeHandler)}
                        </div>
                    </div>
                </div>
                <div className={classNames(formStyle.Cell, formStyle.DateCell)}>
                    <div className={formStyle.CellContent}>
                        <InputWithValidation
                            inputContainerClassNames={style.DateTimeButtonContainer}
                            name="date"
                            validationErrors={validationErrors}
                        >
                            <ButtonLaunchingPopover
                                buttonClassName={classNames(style.Input, style.DateTimeButton, {[style.Placeholder]: !selectedDate})}
                                childComponentFactory={closePopover =>
                                    this.renderDateInputPopoverContent(selectedDate, previousLegDate, maxDate, closePopover)}
                                popoverHorizontalAlignment="center"
                                popoverBodyClassName={classNames(style.Popover, style.DatePopover)}
                                closeOnClickOutside={true}
                                focusOnShow={true}
                            >
                                {(!!selectedDate
                                    ? <FormattedDate date={DateTime.FromDate(selectedDate)} style={DateStyle.Medium} />
                                    : intl.formatMessage(translations.datePlaceholder))}
                            </ButtonLaunchingPopover>
                        </InputWithValidation>
                    </div>
                </div>
                <div className={classNames(formStyle.Cell, formStyle.TimeCell)}>
                    <div className={formStyle.CellContent}>
                        <InputWithValidation
                            inputContainerClassNames={style.DateTimeButtonContainer}
                            name="time"
                            validationErrors={validationErrors}
                        >
                            <ButtonLaunchingPopover
                                buttonClassName={classNames(style.Input, style.DateTimeButton, {[style.Placeholder]: !selectedTime})}
                                childComponentFactory={closePopover =>
                                    this.renderTimeInputPopoverContent(selectedTime, previousLegDate, closePopover, locale)}
                                popoverHorizontalAlignment="center"
                                popoverBodyClassName={classNames(style.Popover, style.TimePopover)}
                                closeOnClickOutside={true}
                                focusOnShow={true}
                            >
                                {(!!selectedTime
                                    ? DateTime.FromDate(selectedTime).getLocallyFormattedTime(locale)
                                    : intl.formatMessage(translations.timePlaceholder))}
                            </ButtonLaunchingPopover>
                        </InputWithValidation>
                    </div>
                </div>
                <div className={classNames(formStyle.Cell, formStyle.PassengerCell)}>
                    <div className={formStyle.CellContent}>
                        <button
                            type="button"
                            onClick={() => this.passengerChangeButtonClicked(passengers - 1)}
                            className={classNames(style.PassengerButton, {[style.Disabled]: passengers <= 1})}
                        >
                            <MinusIcon />
                        </button>
                        <div className={style.PassengerInputContainer}>
                            <InputWithValidation
                                inputContainerClassNames={style.PassengerInput}
                                name="passengers"
                                value={passengers}
                                type="number"
                                min={1}
                                onChange={this.getDefaultChangeHandler(value => parseInt(value, 10) || 0)}
                                validationErrors={validationErrors}
                            />
                            <div className={style.PassengerInputMobileLabel}>
                                {passengers === 1
                                    ? <FormattedMessage
                                        id={'components.flightRequest.form.flightRequestLegForm.mobilePassengerInputSingular'}
                                        defaultMessage={'PASSENGER'}
                                    />
                                    : <FormattedMessage
                                        id={'components.flightRequest.form.flightRequestLegForm.mobilePassengerInputPlural'}
                                        defaultMessage={'PASSENGERS'}
                                    />}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => this.passengerChangeButtonClicked(passengers + 1)}
                            className={style.PassengerButton}
                        >
                            <PlusIcon />
                        </button>
                    </div>
                </div>
                <div className={classNames(formStyle.Cell, formStyle.NotesCell, {[formStyle.NoRemoveCell]: !this.props.showRemoveButton})}>
                    <div className={formStyle.CellContent}>
                        <ButtonLaunchingPopover
                            buttonClassName={classNames(style.LegNotesButton, {[style.Add]: !specialRequests})}
                            childComponentFactory={closePopover => this.renderNotesInputPopoverContent(specialRequests, closePopover)}
                            popoverHorizontalAlignment="end"
                            popoverBodyClassName={classNames(style.Popover, style.NotesPopover)}
                            closeOnClickOutside={true}
                            focusOnShow={true}
                        >
                            {!!specialRequests
                                ? (
                                    <div className={style.LegNotesButtonContent}>
                                        <NotesIcon variation={NotesIconVariation.View}/>
                                        <div className={style.NotesButtonTextShort}>
                                            <FormattedMessage
                                                id={'components.flightRequest.form.flightRequestLegForm.notes.viewSpecialRequests'}
                                                defaultMessage={'VIEW'}
                                            />
                                        </div>
                                        <div className={style.NotesButtonTextLong}>
                                            <FormattedMessage
                                                id={'components.flightRequest.form.flightRequestLegForm.notes.viewAdditionalRequests'}
                                                defaultMessage={'VIEW ADDITIONAL REQUESTS'}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className={style.LegNotesButtonContent}>
                                        <NotesIcon variation={NotesIconVariation.Add}/>
                                        <div className={style.NotesButtonTextShort}>
                                            <FormattedMessage
                                                id={'components.flightRequest.form.flightRequestLegForm.notes.add'}
                                                defaultMessage={'ADD'}
                                            />
                                        </div>
                                        <div className={style.NotesButtonTextLong}>
                                            <FormattedMessage
                                                id={'components.flightRequest.form.flightRequestLegForm.notes.addAdditionalRequests'}
                                                defaultMessage={'ADD ADDITIONAL REQUESTS'}
                                            />
                                        </div>
                                    </div>
                                )
                            }
                        </ButtonLaunchingPopover>
                    </div>
                </div>
                <div
                    className={classNames(formStyle.Cell, formStyle.RemoveCell, {
                        [formStyle.NoButtons]: !this.props.showRemoveButton && !this.props.showDoneButton
                    })}
                >
                    <div className={classNames(formStyle.CellContent, style.LegButtonsContainer)}>
                        {this.props.showRemoveButton &&
                        <button
                            type="button"
                            className={classNames(style.RemoveLegButton, {[style.Disabled]: !this.props.canRemove})}
                            onClick={() => this.props.onRemove()}
                        >
                            <CrossIcon/>
                            <div className={style.RemoveLegButtonText}>
                                <FormattedMessage
                                    id={'components.flightRequest.form.flightRequestLegForm.removeLeg'}
                                    defaultMessage={'REMOVE LEG'}
                                />
                            </div>
                        </button>
                        }
                        {this.props.showDoneButton &&
                        <button
                            type="button"
                            className={style.LegDoneButton}
                            onClick={() => this.props.onDone()}
                        >
                            <FormattedMessage
                                id={'components.flightRequest.form.flightRequestLegForm.done'}
                                defaultMessage={'DONE'}
                            />
                        </button>
                        }
                    </div>
                </div>
            </div>
        );
    }
}

function mapStateToProps(state: AppState): InjectedStateProps {
    return {
        currentLocale: state.intl.locale
    };
}

export const FlightRequestLegForm = connect(mapStateToProps)(injectIntl(FlightRequestLegFormWithIntl));
